import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Store, Users, Receipt, CheckCircle2, Plus, Loader2, Power, Upload } from "lucide-react";
import { api, apiError, fileUrl } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, StatCard, EmptyState, shortDate } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

export function AdminOverview() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
  });
  return (
    <AppLayout>
      <PageHeader eyebrow="Admin" title="Overview" subtitle="Onboard stores, manage roles and keep an eye on the platform." />
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label="Stores" value={stats.stores} icon={Store} />
          <StatCard label="Users" value={stats.users} icon={Users} accent="bg-brand-muted text-brand" />
          <StatCard label="Retailers" value={stats.retailers} icon={Users} accent="bg-warning/12 text-warning" />
          <StatCard label="Receipts" value={stats.receipts} icon={Receipt} />
          <StatCard label="Claimed" value={stats.claimed} icon={CheckCircle2} accent="bg-success/12 text-success" />
        </div>
      )}
    </AppLayout>
  );
}

export function AdminStores() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", abn: "", category: "Electronics", email: "", phone: "", address: "", logo_path: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: data.path }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const { data: stores = [], isLoading } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => (await api.get("/stores")).data,
  });

  const create = async () => {
    if (!form.name) return toast.error("Store name is required.");
    setBusy(true);
    try {
      await api.post("/stores", form);
      toast.success("Store onboarded");
      setOpen(false); setForm({ name: "", abn: "", category: "Electronics", email: "", phone: "", address: "", logo_path: "" });
      qc.invalidateQueries({ queryKey: ["stores"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const toggle = async (id) => {
    await api.post(`/stores/${id}/toggle`);
    qc.invalidateQueries({ queryKey: ["stores"] });
  };

  return (
    <AppLayout>
      <PageHeader eyebrow="Admin" title="Stores" subtitle="Onboard retailers' stores onto ProofPass."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-brand text-white hover:bg-brand-hover" data-testid="new-store-btn"><Plus className="mr-2 h-4 w-4" />Onboard store</Button></DialogTrigger>
            <DialogContent data-testid="new-store-dialog">
              <DialogHeader><DialogTitle className="font-heading">Onboard a store</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div className="flex items-center gap-4">
                  <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-secondary">
                    {form.logo_path ? <img src={fileUrl(form.logo_path)} alt="logo" className="h-full w-full object-cover" /> : <Store className="h-6 w-6 text-muted-foreground" />}
                  </div>
                  <div>
                    <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogo} data-testid="store-logo-input" />
                    <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                      {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      {form.logo_path ? "Change logo" : "Upload logo"}
                    </Button>
                    <p className="mt-1 text-xs text-muted-foreground">Shown on this store's receipts & QR pages.</p>
                  </div>
                </div>
                <div><Label>Store name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="store-name-input" placeholder="Volt Electronics" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label><Input value={form.category} onChange={set("category")} className="mt-1.5" data-testid="store-category-input" /></div>
                  <div><Label>ABN</Label><Input value={form.abn} onChange={set("abn")} className="mt-1.5" data-testid="store-abn-input" /></div>
                </div>
                <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1.5" data-testid="store-email-input" /></div>
                <div><Label>Address</Label><Input value={form.address} onChange={set("address")} className="mt-1.5" data-testid="store-address-input" /></div>
                <Button onClick={create} disabled={busy} className="bg-brand text-white hover:bg-brand-hover" data-testid="create-store-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Onboard store"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      {isLoading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : stores.length === 0 ? (
        <EmptyState icon={Store} title="No stores yet" subtitle="Onboard your first store to start issuing receipts." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {stores.map((s) => (
            <div key={s.id} className="flex items-start justify-between rounded-xl border border-border bg-card p-5" data-testid={`store-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-secondary">
                  {s.logo_path ? <img src={fileUrl(s.logo_path)} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-heading text-lg font-bold">{s.name}</p>
                    <Badge variant={s.active ? "default" : "secondary"} className={s.active ? "bg-success/15 text-success hover:bg-success/15" : ""}>{s.active ? "Active" : "Disabled"}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{s.category} · {s.abn || "No ABN"}</p>
                  <p className="text-xs text-muted-foreground">{s.address || s.email || ""} · added {shortDate(s.created_at)}</p>
                </div>
              </div>
              <button onClick={() => toggle(s.id)} className="text-muted-foreground hover:text-foreground" data-testid={`toggle-store-${s.id}`}><Power className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}

export function AdminUsers() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: async () => (await api.get("/stores")).data });

  const [edit, setEdit] = useState(null);
  const [role, setRole] = useState("customer");
  const [storeId, setStoreId] = useState("none");
  const [busy, setBusy] = useState(false);

  const openEdit = (u) => { setEdit(u); setRole(u.role); setStoreId(u.store_id || "none"); };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${edit.user_id}/role`, { role, store_id: role === "retailer" && storeId !== "none" ? storeId : null });
      toast.success("Role updated");
      setEdit(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const storeName = (id) => stores.find((s) => s.id === id)?.name;

  return (
    <AppLayout>
      <PageHeader eyebrow="Admin" title="Users & roles" subtitle="Assign retailer access and link staff to their store." />
      {isLoading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="divide-y divide-border">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`user-${u.user_id}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{u.name || "—"}</p>
                  <p className="truncate text-xs text-muted-foreground">{u.email}{u.store_id ? ` · ${storeName(u.store_id) || "store"}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="capitalize">{u.role}</Badge>
                  <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}>Edit role</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-role-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit role · {edit?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1.5" data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="retailer">Retailer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {role === "retailer" && (
              <div>
                <Label>Linked store</Label>
                <Select value={storeId} onValueChange={setStoreId}>
                  <SelectTrigger className="mt-1.5" data-testid="store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No store</SelectItem>
                    {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={save} disabled={busy} className="w-full bg-brand text-white hover:bg-brand-hover" data-testid="save-role-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
